import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * EEMENTOR product card: Add to cart for [data-ee-add-form] forms rendered by snippets/ee-product-card.liquid.
 * Dispatches the standard CartLinesUpdateEvent so Horizon's cart drawer and cart count update natively.
 * Delegated on document, so it covers every card on the page (and cards rendered later).
 */

/* global Theme */
const routes = typeof Theme !== 'undefined' ? Theme.routes : {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_URL = routes.cart_url || '/cart';

let status;
function announce(message) {
  if (!status) {
    status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    document.body.append(status);
  }
  status.textContent = message;
}

/** @param {HTMLFormElement} form */
async function addToCart(form) {
  const button = form.querySelector('button');
  const label = button.querySelector('.ee-card__btn-label') || button;
  const original = label.textContent;
  button.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((r) => r.json());

    if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

    const cart = await fetch(`${CART_URL}.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'ee-product-card',
        sourceId: form.closest('[id^="shopify-section-"]')?.id ?? '',
        itemCount: quantity,
        sections: response.sections,
        didError: false,
      },
    });

    label.textContent = 'Added';
    button.dataset.added = '';
    announce('Added to cart');
    setTimeout(() => {
      label.textContent = original;
      delete button.dataset.added;
    }, 2000);
  } catch (error) {
    deferred.reject(error);
    form.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    announce(error.message);
    label.textContent = 'Try again';
    setTimeout(() => (label.textContent = original), 2500);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

if (!window.__eeProductCardBound) {
  window.__eeProductCardBound = true;
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ee-add-form]')) return;
    event.preventDefault();
    addToCart(form);
  });
}
